import { ReactNode } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface OnboardingGuardProps {
  children: ReactNode;
}

/**
 * Protects routes from users who haven't completed onboarding.
 * Redirects to /onboarding if onboarding_completed is false.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { status, profile, profileLoaded, isHydrated } = useAuth();
  const location = useLocation();

  // Still loading auth state
  if (status === "loading" || status === "hydrating" || !isHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="text-sm">Chargement…</span>
        </div>
      </div>
    );
  }

  // Timeout - show retry screen instead of auto-redirect to prevent loops
  if (status === "timeout") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <RefreshCw className="w-8 h-8 text-muted-foreground" />
          <div>
            <h2 className="font-semibold text-lg mb-1">Session lente</h2>
            <p className="text-sm text-muted-foreground">
              La connexion prend plus de temps que prévu.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Réessayer
            </Button>
            <Button asChild>
              <Link to="/auth">Se connecter</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to auth
  if (status === "unauthenticated") {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Profile not loaded yet - wait
  if (!profileLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="text-sm">Préparation…</span>
        </div>
      </div>
    );
  }

  // Authenticated, profile loaded, but onboarding not completed - redirect to onboarding
  // Only redirect if explicitly false, not if undefined (for existing users without this field)
  if (profile && profile.onboarding_completed === false) {
    return <Navigate to="/onboarding" replace />;
  }

  // All good - render children (including if profile is null after loading attempt)
  return <>{children}</>;
}
